import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getBookmarkedOpinions } from 'app/dispatcher/opinionDispatcher';

import { FilterBar } from 'app/seedly-component-library';
import PostList from 'app/components/post/PostList';
import OpinionList from 'app/components/opinion/OpinionList';
import ArticlePreviewList from 'app/components/blog/home/ArticlePreviewList';
import CommentListWithEntity from 'app/components/comment/CommentListWithEntity';
import { useUserComments } from 'entity/comment/commentHooks';
import { useIsSignedIn } from 'app/entity/user/userHooks';
import useGetCommunityArticles from 'app/entity/blog/hooks/useGetCommunityArticles';
import { getBookmarkedPostsRequest } from 'entity/post/postRedux';
import buildConfig from 'app/utils/buildConfig';
import blogApi from 'app/entity/blog/blogApi';
import {
  getUserBookmarkedParentCommentsRequest,
  getUserBookmarkedChildCommentsRequest,
} from 'entity/comment/commentRedux';
import useIsNativeApp from 'utils/hooks/useIsNativeApp';
import useIsMobile from 'utils/hooks/useIsMobile';
import profileApi from 'app/api/profileApi';
import { SAVE_TYPE } from 'app/constants/app';
import { imageUrl } from 'theme';
import DiscoveryContentCard from './DiscoveryContentCard';

const SavedContent = () => {
  const options = Object.values(SAVE_TYPE);
  const [savedCount, setSavedCount] = useState({
    savedParentCommentsCount: 0,
    savedChildCommentsCount: 0,
    savedArticlesCount: 0,
    savedOpinionsCount: 0,
    savedQuestionsCount: 0,
  });

  const dispatch = useDispatch();
  const isSignedin = useIsSignedIn();
  const isNativeApp = useIsNativeApp();
  const isMobile = useIsMobile();

  const { slug, id } = useSelector(state => state.auth.user);
  const pagination = useSelector(state => state.post.pagination);
  const { pagination: commentPagination } = useUserComments();
  const {
    articles,
    handleLoadArticles,
    handleLoadMoreArticles,
    loadingArticles,
    loadingMoreArticles,
    haveMorePage,
  } = useGetCommunityArticles();

  const [selected, setSelected] = useState(null);

  const handleOnSelect = option => {
    setSelected(option);
    let getContentAction;
    switch (option) {
      case SAVE_TYPE.OPINION:
        getContentAction = getBookmarkedOpinions;
        break;
      case SAVE_TYPE.ANSWER:
        getContentAction = getUserBookmarkedParentCommentsRequest;
        break;
      case SAVE_TYPE.POST:
        getContentAction = getBookmarkedPostsRequest;
        break;
      case SAVE_TYPE.REPLY:
        getContentAction = getUserBookmarkedChildCommentsRequest;
        break;
      case SAVE_TYPE.ARTICLE:
        handleLoadArticles(blogApi.getBookmarkedArticles, { userSlug: slug });
        break;
      default:
        getContentAction = getBookmarkedPostsRequest;
    }
    if (getContentAction) dispatch(getContentAction({ slug }));
  };

  const contentCounts = {
    [SAVE_TYPE.OPINION]: savedCount.savedOpinionsCount,
    [SAVE_TYPE.ANSWER]: savedCount.savedParentCommentsCount,
    [SAVE_TYPE.POST]: savedCount.savedQuestionsCount,
    [SAVE_TYPE.ARTICLE]: savedCount.savedArticlesCount,
    [SAVE_TYPE.REPLY]: savedCount.savedChildCommentsCount,
  };

  useEffect(() => {
    const fetchSavedCount = async () => {
      const savedCountResponse = await profileApi.getSavedCount(id);
      if (savedCountResponse.ok) {
        setSavedCount(savedCountResponse.data.data);
      }
    };

    if (id) {
      fetchSavedCount(id);
    }
  }, [id]);

  useEffect(() => {
    if (savedCount.savedArticlesCount !== 0) {
      setSelected(SAVE_TYPE.ARTICLE);
      handleLoadArticles(blogApi.getBookmarkedArticles, { userSlug: slug });
    } else if (savedCount.savedOpinionsCount !== 0) {
      setSelected(SAVE_TYPE.OPINION);
      dispatch(getBookmarkedOpinions({ slug }));
    } else if (savedCount.savedQuestionsCount !== 0) {
      setSelected(SAVE_TYPE.POST);
      dispatch(getBookmarkedPostsRequest({ slug, page: 1 }));
    } else if (savedCount.savedParentCommentsCount !== 0) {
      setSelected(SAVE_TYPE.ANSWER);
      dispatch(getUserBookmarkedParentCommentsRequest({ slug, page: 1 }));
    } else if (savedCount.savedChildCommentsCount !== 0) {
      setSelected(SAVE_TYPE.REPLY);
      dispatch(getUserBookmarkedChildCommentsRequest({ slug, page: 1 }));
    } else {
      setSelected(SAVE_TYPE.ARTICLE);
    }
  }, [savedCount]);

  const filterTypes = options.map(option => {
    const title = `${option.charAt(0).toUpperCase() + option.slice(1)}s (${
      contentCounts[option]
    })`;
    const value = option;
    return {
      title,
      value,
    };
  });

  return (
    <div className="flex flex-col w-full items-center">
      {!isNativeApp && (
        <div
          style={{
            backgroundImage: `url(${
              isMobile
                ? imageUrl.homePageCoverMobile
                : imageUrl.homePageCoverDesktop
            })`,
          }}
          className="h-[280px] w-full center-in-div bg-blue-500 mb-3 bg-no-repeat bg-cover bg-center"
        >
          <div className="flex flex-col items-center gap-3">
            <p className="text-white font-extrabold text-4xl">Saved</p>
            <p className="text-white">Your reading list</p>
          </div>
        </div>
      )}
      <div className="flex flex-col items-center w-full lg:w-[792px]">
        <div className="flex flex-col gap-1 items-center w-full">
          <div className="bg-white rounded w-full p-4">
            <FilterBar
              filterList={filterTypes}
              selected={selected}
              onSelect={filterItem => handleOnSelect(filterItem.value)}
              listSpacing="lg"
            />
          </div>
          {selected === SAVE_TYPE.OPINION && isSignedin && (
            <OpinionList
              customEmpty={
                <DiscoveryContentCard
                  contentType="an Opinion"
                  discoveryRoute="/community/?articles=true"
                  buttonTitle="Discover Opinions"
                />
              }
            />
          )}
          {selected === SAVE_TYPE.ANSWER && isSignedin && (
            <CommentListWithEntity
              customEmpty={
                <DiscoveryContentCard
                  contentType="a comment"
                  discoveryRoute="/community"
                />
              }
              handleLoadMoreComment={() => {
                dispatch(
                  getUserBookmarkedParentCommentsRequest({
                    page: commentPagination?.currentPage + 1,
                    slug,
                  }),
                );
              }}
            />
          )}
          {selected === SAVE_TYPE.REPLY && isSignedin && (
            <CommentListWithEntity
              customEmpty={
                <DiscoveryContentCard
                  contentType="a reply"
                  discoveryRoute="/community"
                />
              }
              handleLoadMoreComment={() => {
                dispatch(
                  getUserBookmarkedChildCommentsRequest({
                    page: commentPagination?.currentPage + 1,
                    slug,
                  }),
                );
              }}
            />
          )}
          {selected === SAVE_TYPE.POST && isSignedin && (
            <PostList
              customEmpty={
                <DiscoveryContentCard
                  contentType="a post"
                  discoveryRoute="/community"
                  buttonTitle="Discover Posts"
                />
              }
              handleLoadMorePost={() => {
                dispatch(
                  getBookmarkedPostsRequest({
                    page: pagination?.currentPage + 1,
                    slug,
                  }),
                );
              }}
            />
          )}
          {selected === SAVE_TYPE.ARTICLE && isSignedin && (
            <ArticlePreviewList
              items={articles}
              loading={loadingArticles}
              loadingMore={loadingMoreArticles}
              onLoadMore={() => {
                handleLoadMoreArticles(blogApi.getBookmarkedArticles, {
                  userSlug: slug,
                });
              }}
              RenderWhenEmpty={
                <DiscoveryContentCard
                  contentType="an article"
                  discoveryRoute={buildConfig.blogHost}
                  buttonTitle="Discover Articles"
                />
              }
              endReached={!haveMorePage}
              spinner
            />
          )}
          {!isSignedin && (
            <DiscoveryContentCard
              contentType="a content"
              buildConfig="/community"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedContent;
